import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { romaneioService } from '../../../../../api';
import { useOps } from '../../../../../hooks/useOps';
import { cleanCnpj, isValidCnpj } from '../../../../../utils';
import { BulkOperationResult, CsvParseResult } from '../types';
import { entregoQueryKeys } from './use-entrego-search';

function validateDocument(value: string): boolean {
  const cleaned = cleanCnpj(value);
  if (cleaned.length === 14) return isValidCnpj(cleaned); // CNPJ alfanumérico (DV)
  return /^\d{11}$/.test(cleaned); // CPF numérico
}

function parseCsvFile(file: File): Promise<CsvParseResult> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const text = event.target?.result as string;
        const lines = text.split(/\r?\n/).filter((line) => line.trim());

        const valid: string[] = [];
        const invalid: { line: number; value: string; reason: string }[] = [];

        lines.forEach((line, index) => {
          const value = line.trim();

          if (!value) {
            return;
          }

          if (
            index === 0 &&
            (value.toLowerCase() === 'cnpj' ||
              value.toLowerCase() === 'cpf' ||
              value.toLowerCase() === 'documento')
          ) {
            return;
          }

          const cleaned = cleanCnpj(value);

          if (!cleaned) {
            invalid.push({
              line: index + 1,
              value,
              reason: 'Valor vazio ou inválido',
            });
            return;
          }

          if (!validateDocument(cleaned)) {
            invalid.push({
              line: index + 1,
              value,
              reason: 'CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos',
            });
            return;
          }

          if (!valid.includes(cleaned)) {
            valid.push(cleaned);
          }
        });

        resolve({ valid, invalid });
      } catch {
        reject(new Error('Erro ao processar arquivo CSV'));
      }
    };

    reader.onerror = () => {
      reject(new Error('Erro ao ler arquivo'));
    };

    reader.readAsText(file);
  });
}

export function useBulkOperations() {
  const [file, setFile] = useState<File | null>(null);
  const [parseResult, setParseResult] = useState<CsvParseResult | null>(null);
  const [operationResult, setOperationResult] =
    useState<BulkOperationResult | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const { getCurrentOpsConfig, currentOps } = useOps();
  const queryClient = useQueryClient();

  const bulkUpdateMutation = useMutation({
    mutationFn: async ({
      documentos,
      active,
    }: {
      documentos: string[];
      active: boolean;
    }) => {
      const opsConfig = getCurrentOpsConfig();
      if (!opsConfig?.dbName) {
        throw new Error('Configuração de banco de dados não encontrada');
      }

      const response = await romaneioService.bulkUpdateEntregoCadastro({
        documentos,
        active,
        dbName: opsConfig.dbName,
        ops: String(currentOps),
      });

      return response.data;
    },
    onSuccess: (data) => {
      setOperationResult(data);
      queryClient.invalidateQueries({ queryKey: entregoQueryKeys.all });
    },
  });

  const handleFileSelect = useCallback(
    async (selectedFile: File) => {
      setFile(selectedFile);
      setIsParsing(true);
      setParseError(null);
      setParseResult(null);
      setOperationResult(null);
      bulkUpdateMutation.reset();

      try {
        const result = await parseCsvFile(selectedFile);
        setParseResult(result);
      } catch (error: any) {
        setParseError(error.message || 'Erro ao processar arquivo');
      } finally {
        setIsParsing(false);
      }
    },
    [bulkUpdateMutation]
  );

  const processOperation = useCallback(
    (action: 'block' | 'unblock') => {
      if (!parseResult?.valid.length) {
        return;
      }

      setOperationResult(null);
      bulkUpdateMutation.mutate({
        documentos: parseResult.valid,
        active: action === 'unblock',
      });
    },
    [parseResult, bulkUpdateMutation]
  );

  const reset = useCallback(() => {
    setFile(null);
    setParseResult(null);
    setOperationResult(null);
    setIsParsing(false);
    setParseError(null);
    bulkUpdateMutation.reset();
  }, [bulkUpdateMutation]);

  const clearMessages = useCallback(() => {
    setParseError(null);
    bulkUpdateMutation.reset();
  }, [bulkUpdateMutation]);

  const generateCsvTemplate = useCallback(() => {
    const template = 'documento\n12345678901\n12345678901234';
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'template-bloqueio-documentos.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  return {
    file,
    parseResult,
    operationResult,
    isParsing,
    isProcessing: bulkUpdateMutation.isPending,
    parseError,
    processError: bulkUpdateMutation.error?.message || null,
    handleFileSelect,
    processOperation,
    reset,
    clearMessages,
    generateCsvTemplate,
  };
}
